import { ArrowDown, ArrowUpRight } from "lucide-react";
import { press } from "@/data/content";
import { Eyebrow, Reveal, SectionTitle } from "./Reveal";

const VideoPlayer = ({ id, title, i }) => (
  <div data-testid={`press-video-${i}`} className="relative aspect-video w-full overflow-hidden bg-black">
    <iframe data-testid={`press-video-iframe-${i}`} className="absolute inset-0 h-full w-full" title={title} loading="lazy"
      src={`https://www.youtube.com/embed/${id}?rel=0&playsinline=1`}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
  </div>
);

const videos = press.filter((p) => p.youtube);
const articles = press.filter((p) => !p.youtube);

const WatchRow = () => (
  <div data-testid="press-watch" className="mb-20">
    <Reveal className="mb-8 flex items-end justify-between gap-6">
      <h3 className="font-display text-3xl sm:text-4xl">Watch<em className="text-[var(--gold)]">.</em></h3>
      <span className="text-[12px] tracking-[0.2em] text-stone-600">{String(videos.length).padStart(2, "0")} INTERVIEWS</span>
    </Reveal>
    <div className="grid md:grid-cols-3 gap-8 sm:gap-6">
      {videos.map((p, i) => (
        <Reveal key={p.youtube} delay={i * 0.08}>
          <article data-testid={`press-watch-${i}`}>
            <VideoPlayer id={p.youtube} title={p.title} i={i} />
            <p className="mt-4 text-[12px] uppercase tracking-[0.2em] text-[var(--gold)]">{p.outlet.replace(" · YouTube", "")}</p>
            <h4 className="mt-2 font-display text-xl leading-snug">{p.title}</h4>
          </article>
        </Reveal>
      ))}
    </div>
  </div>
);

const PressPhoto = ({ p, i }) => (
  <figure data-testid={`press-photo-${i}`} className={`relative shrink-0 w-2/3 sm:w-[42%] ${p.photoRatio || "aspect-[2/3]"} overflow-hidden sm:order-last shadow-[0_24px_50px_-28px_rgba(60,40,20,0.5)]`}>
    <img src={p.photo} alt={`${p.outlet} — ${p.title}`} loading="lazy" style={{ objectPosition: p.photoPos || "50% 50%" }}
      className="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms] ease-out" />
    {p.photoLogo !== false && (
      <img src={p.logo} alt={p.outlet} data-testid={`press-photo-logo-${i}`}
        className="absolute left-3 top-3 h-4 sm:h-5 w-auto brightness-0 invert opacity-90 drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]" />
    )}
  </figure>
);

export const Press = () => (

  <section id="press" data-testid="press-section" className="max-w-[1500px] mx-auto px-5 sm:px-10 pt-36 sm:pt-44 pb-28">
    <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
      <div>
        <Eyebrow className="mb-6">04 — Press</Eyebrow>
        <SectionTitle>In the <em className="text-[var(--gold)]">headlines</em>.</SectionTitle>
      </div>
      <div className="self-start md:self-auto flex flex-wrap gap-3">
        <a data-testid="press-kit-btn" href="/mimi-tao-press-kit.pdf" download="Mimi-Tao-Press-Kit.pdf"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-white hover:bg-[var(--gold)] transition-colors duration-500">
          Press kit <ArrowDown size={14} />
        </a>
        <a data-testid="press-resume-btn" href="/mimi-tao-resume.pdf" download="Mimi-Tao-Resume.pdf"
          className="inline-flex items-center gap-2 rounded-full border border-[var(--ink)] px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-white transition-colors duration-500">
          Résumé <ArrowDown size={14} />
        </a>
      </div>
    </Reveal>
    <WatchRow />
    <Reveal className="mb-8"><h3 className="font-display text-3xl sm:text-4xl">Read<em className="text-[var(--gold)]">.</em></h3></Reveal>
    <div className="grid sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
      {articles.map((p, i) => (
        <Reveal key={i} delay={(i % 2) * 0.1} className={`bg-[var(--bg)] ${articles.length % 2 && i === articles.length - 1 ? "sm:col-span-2" : ""}`}>
          <article data-testid={`press-card-${i}`} className={`relative isolate overflow-hidden group h-full p-8 sm:p-12 flex ${p.photo ? "flex-col sm:flex-row gap-8" : "flex-col justify-between"} min-h-[280px] hover:bg-[var(--nude)] transition-colors duration-500`}>
            {p.logo && !p.photo && (
              <img src={p.logo} alt="" aria-hidden data-testid={`press-logo-${i}`}
                className="pointer-events-none select-none absolute -z-10 right-6 top-1/2 -translate-y-1/2 w-[70%] max-h-[55%] object-contain object-right opacity-[0.28] group-hover:opacity-[0.4] group-hover:scale-[1.03] transition-[opacity,transform] duration-700" />
            )}
            {p.photo && <PressPhoto p={p} i={i} />}
            <div className="flex-1 flex flex-col justify-between gap-8">
            <div className="flex justify-between text-[11px] uppercase tracking-[0.25em] text-stone-500">
              <span>{p.tag}</span><span>{p.year}</span>
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-[var(--gold)] mb-4">{p.outlet}</p>
              <h3 className="font-display text-2xl sm:text-3xl leading-snug group-hover:italic transition-all">{p.title}</h3>
              {p.href && (
                <a href={p.href} target="_blank" rel="noreferrer" data-testid={`press-link-${i}`}
                  className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--gold)] link-line after:absolute after:inset-0">
                  {p.cta || "Read the article"} <ArrowUpRight size={14} />
                </a>
              )}
            </div>
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  </section>
);
